import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const AffiliatesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Smooth scrolling
    const handleAnchorClick = (e) => {
      const href = e.target.closest('a')?.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    // Dynamic nav background
    const handleScroll = () => {
      const nav = document.querySelector('nav');
      if (nav) {
        nav.style.boxShadow = window.scrollY > 10 ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none';
      }
    };

    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 py-6 transition-shadow">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-3xl font-extrabold">
              <span className="bg-gradient-to-r from-indigo-600 to-emerald-500 bg-clip-text text-transparent">
                Alfred Voice
              </span>
              <span className="text-gray-500 text-sm ml-2 font-normal">Affiliates</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-gray-600 hover:text-indigo-600 font-medium">Home</Link>
              <a href="#commission" className="text-gray-600 hover:text-indigo-600 font-medium">Commission</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-indigo-600 font-medium">How It Works</a>
              <a href="#terms" className="text-gray-600 hover:text-indigo-600 font-medium">Terms</a>
              <Link to="/support" className="text-gray-600 hover:text-indigo-600 font-medium">Support</Link>
              <Link
                to="/affiliates/apply"
                className="bg-gradient-to-r from-indigo-600 to-emerald-500 text-white px-6 py-2 rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-500 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:50px_50px] animate-pulse"></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
            Earn Up to $700+ Per Customer
          </h1>
          <p className="text-xl md:text-2xl mb-10 opacity-90 leading-relaxed">
            Join the Alfred Voice affiliate program and earn generous recurring commissions promoting premium AI customer care. High-converting products, professional support, and industry-leading payouts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/affiliates/apply"
              className="inline-block bg-white text-indigo-600 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all"
            >
              Apply to Join
            </Link>
            <a
              href="#commission"
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-indigo-600 transition-all"
            >
              View Commission Structure
            </a>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '$700+', label: 'Potential per customer' },
              { value: '12', label: 'Month commission period' },
              { value: '60', label: 'Day cookie duration' },
              { value: '30%', label: 'Max commission rate' }
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <div className="text-4xl font-extrabold mb-2">{stat.value}</div>
                <div className="text-sm opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Structure */}
      <section id="commission" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-extrabold mb-4 bg-gradient-to-r from-indigo-600 to-emerald-500 bg-clip-text text-transparent">
              Generous Commission Structure
            </h2>
            <p className="text-xl text-gray-600">
              Earn recurring commissions for 12 full months from each customer you refer
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Core Package',
                badgeClass: 'from-indigo-600 to-emerald-500',
                price: '$149/month',
                commission: '30% recurring',
                payout: '$44.70/month',
                total: '$536.40 total per customer',
                bullets: [
                  '100 calls/month (30s max each)',
                  'AI answers on the first ring',
                  'Call forwarding to the owner\'s phone',
                  'Automatic message + email summaries',
                  'Answers hours, directions, and common FAQs'
                ]
              },
              {
                name: 'Premium Package',
                badgeClass: 'from-emerald-500 to-emerald-600',
                price: '$449/month',
                commission: '25% recurring',
                payout: '$112.25/month',
                total: '$1,347 total per customer',
                bullets: [
                  '600 calls/month (45s max each)',
                  'Includes every Core feature',
                  'Multi-route calls across teams',
                  'Capture orders or appointment details',
                  'Structured data capture (name, number, intent)',
                  'Searchable call history with email summaries'
                ],
                featured: true
              },
              {
                name: 'Enterprise Package',
                badgeClass: 'from-indigo-600 to-emerald-500',
                price: '$899/month',
                commission: '20% recurring',
                payout: '$179.80/month',
                total: '$2,157.60 total per customer',
                bullets: [
                  '1,500 calls/month (45s max each)',
                  'All Premium capabilities included',
                  'Enterprise call routing across locations',
                  'Advanced FAQ + knowledge base integration',
                  'Full transcription archive for QA/compliance',
                  'Priority forwarding and escalation workflows'
                ]
              }
            ].map(plan => (
              <div
                key={plan.name}
                className={
                  plan.featured
                    ? 'bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-3xl text-center hover:-translate-y-3 hover:shadow-2xl transition-all border-3 border-emerald-500 relative'
                    : 'bg-gray-50 p-8 rounded-3xl text-center hover:-translate-y-3 hover:shadow-2xl transition-all border-3 border-transparent'
                }
              >
                <div className={`inline-block bg-gradient-to-r ${plan.badgeClass} text-white px-6 py-2 rounded-full text-sm font-semibold mb-6`}>
                  {plan.name}
                </div>
                {plan.featured && (
                  <span className="absolute top-8 right-8 text-xs font-semibold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <div className="text-xl text-gray-600 mb-2">{plan.price}</div>
                <div className="text-lg text-emerald-600 font-semibold mb-4">{plan.commission}</div>
                <div className="text-2xl font-bold mb-2 text-gray-900">{plan.payout}</div>
                <div className="text-sm text-gray-500 mb-8">{plan.total}</div>
                <ul className="text-left space-y-3">
                  {plan.bullets.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="text-emerald-500 text-xl font-bold">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-extrabold mb-4 bg-gradient-to-r from-indigo-600 to-emerald-500 bg-clip-text text-transparent">
              How Our Affiliate Program Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple process, professional support, and transparent tracking
            </p>
          </div>

          <div className="space-y-12">
            {[
              { num: '1', title: 'Apply to Join', desc: 'Submit your application with details about your audience and promotional methods. We review applications within 48 hours and approve qualified partners who align with our brand values.' },
              { num: '2', title: 'Get Your Affiliate Links', desc: 'Access your personalized affiliate dashboard with unique tracking links, promotional materials, and real-time performance analytics. We provide everything you need to succeed.' },
              { num: '3', title: 'Promote Alfred Voice', desc: 'Share Alfred Voice with your audience through blog posts, social media, email campaigns, or any approved promotional method. Our 60-day cookie ensures you get credit for referrals.' },
              { num: '4', title: 'Earn Recurring Commissions', desc: 'Receive monthly payouts for 12 full months from each customer you refer. Track your earnings in real-time and get paid via PayPal, Stripe, or wire transfer.' }
            ].map((step, i) => (
              <div key={i} className={`flex items-center gap-8 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-emerald-500 rounded-full flex items-center justify-center text-white text-3xl font-extrabold flex-shrink-0">
                  {step.num}
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section id="terms" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-extrabold mb-4 bg-gradient-to-r from-indigo-600 to-emerald-500 bg-clip-text text-transparent">
              Program Terms & Details
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our affiliate program
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Commission Details',
                items: [
                  'Commission Period: 12 months from customer\'s first payment',
                  'Cookie Duration: 60 days from click',
                  'Payment Schedule: Monthly within 30 days',
                  'Payment Threshold: $50 minimum',
                  'Payment Methods: PayPal, Stripe, Wire Transfer',
                  'Currency: USD'
                ]
              },
              {
                title: 'Affiliate Requirements',
                items: [
                  'Quality content and professional presentation',
                  'Genuine audience interested in AI/customer service',
                  'Comply with FTC disclosure guidelines',
                  'No misleading claims about our service',
                  'No spam or unethical promotional methods',
                  'Maintain brand standards in all content'
                ]
              },
              {
                title: 'What We Provide',
                items: [
                  'Personalized affiliate dashboard',
                  'Real-time tracking and analytics',
                  'High-quality promotional materials',
                  'Dedicated affiliate support team',
                  'Regular program updates and training',
                  'Competitive commission structure'
                ]
              },
              {
                title: 'Prohibited Activities',
                items: [
                  'Bidding on trademarked keywords',
                  'Direct competitor advertising',
                  'Misleading or false claims',
                  'Spam or unsolicited communications',
                  'Adult or inappropriate content association',
                  'Brand trademark misuse'
                ]
              }
            ].map((section, i) => (
              <div key={i} className="bg-gray-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6">{section.title}</h3>
                <ul className="space-y-3 text-gray-600">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-50 p-8 rounded-2xl">
            <h3 className="text-xl font-bold mb-4">Legal Information</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              This affiliate program is operated by <strong>Mule Works Inc. dba Relay Work</strong>, located in Houston, Texas, United States.
              By participating in this program, affiliates agree to our complete terms and conditions, privacy policy, and applicable laws.
              We reserve the right to modify these terms or terminate affiliate relationships at our discretion.
              All commissions are subject to customer payment verification and our standard refund policies.
            </p>
            <p className="text-gray-600 leading-relaxed">
              <strong>Tax Responsibility:</strong> Affiliates are responsible for all applicable taxes on commission payments.
              <strong className="ml-4">Contact:</strong> For questions about our affiliate program, contact hello@relaywork.co
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="apply" className="py-20 bg-gradient-to-br from-indigo-600 to-emerald-500 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:30px_30px]"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-5xl font-extrabold mb-6">Ready to Start Earning?</h2>
          <p className="text-xl mb-10 opacity-90">
            Join our affiliate program today and start earning generous commissions promoting cutting-edge AI customer care. Apply now and get approved within 48 hours.
          </p>
          <Link
            to="/affiliates/apply"
            className="inline-block bg-white text-indigo-600 px-10 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all"
          >
            Complete Application Form
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h4 className="text-xl font-bold mb-4">Alfred Voice Affiliates</h4>
              <p className="text-gray-400 mb-4">Premium AI Customer Care</p>
              <p className="text-gray-500 text-sm">Part of Relay Work by Mule Works Inc</p>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4">Affiliate Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/affiliates/apply" className="text-gray-400 hover:text-white">Apply to Join</Link></li>
                <li><a href="#commission" className="text-gray-400 hover:text-white">Commission Structure</a></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white">How It Works</a></li>
                <li><Link to="/support" className="text-gray-400 hover:text-white">Support</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4">Alfred Voice</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white">Main Site</Link></li>
                <li><a href="/#features" className="text-gray-400 hover:text-white">Features</a></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/affiliate-terms" className="text-gray-400 hover:text-white">Affiliate Terms</a></li>
                <li><a href="https://relaywork.co/privacy.html" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">Privacy Policy</a></li>
                <li><a href="https://relaywork.co/terms.html" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white">Terms of Service</a></li>
                <li><a href="https://relaywork.co" className="text-gray-400 hover:text-white">Relay Work</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>&copy; 2025 Alfred Voice Affiliate Program by Relay Work (Mule Works Inc.). All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AffiliatesPage;
